import { Tour as ArkTour, type TourStepDetails, type UseTourReturn } from "@ark-ui/solid";
import { IconButton, TextButton } from "../button/Button";
import type { VoidComponent } from "solid-js";
import { createSignal, For, Show } from "solid-js";
import { createAutofocus } from "@solid-primitives/autofocus";
import { DECORATIVE_IMAGE_ALT } from "../../constants";
import { useTranslation } from "../../state/i18n";
import type { StepAction } from "@zag-js/tour";
import Portal from "../../utils/Portal";

export type StepDetailsWithMeta = {
  meta: {
    image: string;
  };
  actions: ({
    attrs?: {
      variant: "primary";
    };
  } & StepAction)[];
} & TourStepDetails;

type TourProps = {
  tour: UseTourReturn;
};
const Tour: VoidComponent<TourProps> = (props) => {
  const { t } = useTranslation();

  return (
    <ArkTour.Root tour={props.tour}>
      <ArkTour.Context>
        {(context) => (
          <Show when={context().open}>
            <Portal>
              <ArkTour.Backdrop class="bg-overlay-default" />
              <ArkTour.Positioner>
                <ArkTour.Content class="flex flex-col rounded-lg bg-surface-default shadow-elevation-light-5">
                  <div class="ml-auto p-1">
                    <ArkTour.CloseTrigger
                      asChild={(closeTriggerProps) => (
                        <IconButton
                          {...closeTriggerProps}
                          icon="x"
                          variant="tertiary"
                          aria-label={t(($) => $.FORM_EDITOR_TOUR_ACTIONS_CLOSE_ARIA_LABEL)}
                        />
                      )}
                    />
                  </div>

                  <Show when={context().step as StepDetailsWithMeta}>
                    {(step) => {
                      // Required to steal back focus from ZagJS focus trap and put it on the primary button
                      const [ref, setRef] = createSignal<HTMLButtonElement>();
                      createAutofocus(ref);

                      return (
                        <div class="flex w-86 flex-col">
                          <div class="h-45">
                            <img src={step().meta.image} alt={DECORATIVE_IMAGE_ALT} />
                          </div>

                          <div class="p-6">
                            <ArkTour.Title class="mb-2 text-header-lg text-primary" />
                            <ArkTour.Description class="text-medium-regular text-secondary" />

                            <div class="mt-6 flex items-center">
                              <ArkTour.ProgressText class="text-medium-regular text-secondary" />
                              <ArkTour.Control class="ml-auto flex gap-2">
                                <For each={step().actions}>
                                  {(action) => (
                                    <ArkTour.ActionTrigger
                                      action={action}
                                      asChild={(actionTriggerProps) => (
                                        <TextButton
                                          {...actionTriggerProps}
                                          aria-label={action.label}
                                          text={action.label}
                                          size="medium"
                                          variant={action.attrs?.variant ?? "secondary"}
                                          {...(action.attrs?.variant === "primary" ? { ref: setRef } : {})}
                                        />
                                      )}
                                    />
                                  )}
                                </For>
                              </ArkTour.Control>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  </Show>
                </ArkTour.Content>
              </ArkTour.Positioner>
            </Portal>
          </Show>
        )}
      </ArkTour.Context>
    </ArkTour.Root>
  );
};

export default Tour;
